import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const landmarks = [
  {
    name: 'Eiffel Tower',
    location: 'Paris',
    destination: 'FR',
    image: 'https://images.unsplash.com/photo-1769374343062-23dd6e672352?w=200&h=200&fit=crop&crop=center&auto=format&q=60',
    top: '5%', left: '5%', delay: '0s',
  },
  {
    name: 'Taj Mahal',
    location: 'India',
    destination: 'IN',
    image: 'https://images.pexels.com/photos/30638768/pexels-photo-30638768.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop',
    top: '52%', left: '0%', delay: '0.8s',
  },
  {
    name: 'Colosseum',
    location: 'Rome',
    destination: 'IT',
    image: 'https://images.unsplash.com/photo-1752886355853-5cc028c80c85?w=200&h=200&fit=crop&crop=center&auto=format&q=60',
    top: '0%', right: '5%', delay: '1.6s',
  },
  {
    name: 'Big Ben',
    location: 'London',
    destination: 'GB',
    image: 'https://images.unsplash.com/photo-1727666463908-34cc8cf4b3f1?w=200&h=200&fit=crop&crop=center&auto=format&q=60',
    top: '48%', right: '0%', delay: '2.4s',
  },
  {
    name: 'Sydney Opera',
    location: 'Australia',
    destination: 'AU',
    image: 'https://images.unsplash.com/photo-1761029542850-93676fb74980?w=200&h=200&fit=crop&crop=center&auto=format&q=60',
    bottom: '2%', right: '15%', delay: '3.2s',
  },
  {
    name: 'Mount Fuji',
    location: 'Japan',
    destination: 'JP',
    image: 'https://images.unsplash.com/photo-1730800328482-38d2d44e0428?w=200&h=200&fit=crop&crop=center&auto=format&q=60',
    bottom: '0%', left: '12%', delay: '4s',
  },
];

const Globe = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const { effectiveTheme } = useTheme();
  const isDark = effectiveTheme === 'dark';

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.8;
    }
  }, [isDark]);

  return (
    <div
      data-testid="hero-globe"
      style={{
        width: 420,
        maxWidth: '90vw',
        position: 'relative',
        margin: '0 auto',
      }}
    >
      {/* Floating landmark pop-ups */}
      {landmarks.map((lm) => (
        <div
          key={lm.name}
          className="landmark-bubble"
          onClick={() => navigate(`/destinations/${lm.destination}`)}
          style={{
            position: 'absolute',
            top: lm.top,
            left: lm.left,
            right: lm.right,
            bottom: lm.bottom,
            zIndex: 10,
            animationDelay: lm.delay,
            cursor: 'pointer',
          }}
          data-testid={`landmark-${lm.destination}`}
        >
          <div style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            overflow: 'hidden',
            border: '2.5px solid white',
            boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
            background: '#e2e8f0',
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}>
            <img
              src={lm.image}
              alt={lm.name}
              loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
          <div className="lm-label" style={{
            background: 'white',
            borderRadius: 8,
            padding: '2px 8px',
            marginTop: 3,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#1e293b', display: 'block', lineHeight: 1.3 }}>{lm.name}</span>
            <span style={{ fontSize: 9, color: '#94a3b8' }}>{lm.location}</span>
          </div>
        </div>
      ))}

      {/* Earth — MP4 video in both modes. Dark uses a version with a black
          background baked in; light uses the original. The dark video ships
          at a wider aspect ratio, so we frame it inside a 1:1 box with
          object-fit:cover to keep the exact same layout footprint as the
          original earth-dark.gif (380×380 → 420×420 rendered). */}
      <div style={{ width: '100%', aspectRatio: '1 / 1', overflow: 'hidden' }}>
        <video
          ref={videoRef}
          key={isDark ? 'dark' : 'light'}
          autoPlay
          loop
          muted
          playsInline
          disablePictureInPicture
          controlsList="nodownload nofullscreen noremoteplayback"
          className="earth-video"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          <source src={isDark ? '/earth-video-dark.mp4' : '/earth-video.mp4'} type="video/mp4" />
        </video>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes floatBubble {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-7px); }
        }
        .landmark-bubble {
          animation: floatBubble 3s ease-in-out infinite;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .landmark-bubble:hover > div:first-child {
          transform: scale(1.12);
          box-shadow: 0 6px 20px rgba(0,0,0,0.25) !important;
        }
        @media (max-width: 640px) {
          .landmark-bubble {
            transform: scale(0.7);
          }
          .landmark-bubble .lm-label {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default Globe;
